/**
 * @voltxampere/engine — JFET Shichman-Hodges DC stamp (n + p channel)
 *
 * SPICE Level 1 JFET model — square-law characteristic per ngspice
 * `jfet/jfetload.c` and Sedra/Smith Ch. 4. Three regimes:
 *
 *   Vov = Vgs − Vt0          (overdrive voltage)
 *
 *   Cutoff   (Vov ≤ 0):
 *     Id = 0, gm = 0, gds = 0
 *
 *   Triode   (0 < Vds < Vov):
 *     Id  = β · (2·Vov·Vds − Vds²) · (1 + λ·Vds)
 *     gm  = 2β · Vds · (1 + λ·Vds)
 *     gds = 2β · (Vov − Vds) · (1 + λ·Vds) + β · (2·Vov·Vds − Vds²) · λ
 *
 *   Saturation (Vds ≥ Vov):
 *     Id  = β · Vov² · (1 + λ·Vds)
 *     gm  = 2β · Vov · (1 + λ·Vds)
 *     gds = β · Vov² · λ
 *
 * Continuity at `Vds = Vov`:
 *   Triode Id(Vov) = β · (2·Vov² − Vov²) · (1+λ·Vov) = β · Vov² · (1+λ·Vov) = Sat ✓
 *
 * P-channel JFET mirror'lar n-channel'i: Vgs/Vds polarity flip,
 * RHS akım contribution negatif. Vt0 schema'da pozitif kabul edilir
 * (P-JFET için kullanıcı +2V verir).
 *
 * Gate akımı YOK (Sprint 23 simplified — ideal JFET DC). Forward-
 * bias gate junction Vgs ≥ 0.5V durumunda gerçek modelden uzaklaşır
 * ama Sprint 23 scope dışı; kullanıcı schematic'i tasarlarken bu
 * sınırı bilir.
 *
 * Copyright © 2026 360 Studio
 * Licensed under MIT (see LICENSE file in repo root)
 */
import { StampParameterError } from "@voltxampere/shared";
import { defineDCStamp } from "../definition.js";
import { getTerminalNetId, getNumericParam } from "../terminal-utils.js";
function nodeVoltage(xPrev, idx) {
    if (idx < 0)
        return 0;
    return xPrev[idx] ?? 0;
}
function jfetStampCommon(ctx, component, polarity) {
    const Beta = getNumericParam(component, "Beta", 1e-4);
    const Vt0 = getNumericParam(component, "Vt0", polarity === "n" ? -2 : 2);
    const Lambda = getNumericParam(component, "Lambda", 0.01);
    const jfetCtx = { stampType: "jfet", componentId: component.id };
    if (!(Beta > 0) || !Number.isFinite(Beta)) {
        throw new StampParameterError(`jfetDCStamp: component "${component.id}" has non-positive Beta (got ${String(Beta)})`, { meta: { ...jfetCtx, parameter: "Beta", received: Beta, expected: "> 0 and finite" } });
    }
    if (!Number.isFinite(Vt0) || !Number.isFinite(Lambda) || Lambda < 0) {
        throw new StampParameterError(`jfetDCStamp: component "${component.id}" has invalid Vt0 / Lambda (got Vt0=${String(Vt0)}, Lambda=${String(Lambda)})`, {
            meta: {
                ...jfetCtx,
                parameter: "Vt0/Lambda",
                received: { Vt0, Lambda },
                expected: "finite, Lambda >= 0",
            },
        });
    }
    const G = ctx.nodeIndex(getTerminalNetId(component, "gate"));
    const D = ctx.nodeIndex(getTerminalNetId(component, "drain"));
    const S = ctx.nodeIndex(getTerminalNetId(component, "source"));
    const sign = polarity === "n" ? 1 : -1;
    const vG = nodeVoltage(ctx.xPrev, G);
    const vD = nodeVoltage(ctx.xPrev, D);
    const vS = nodeVoltage(ctx.xPrev, S);
    const Vgs = sign * (vG - vS);
    const Vds = sign * (vD - vS);
    const Vth = sign * Vt0;
    const Vov = Vgs - Vth;
    let Id = 0;
    let gm = 0;
    let gds = 0;
    if (Vov > 0) {
        if (Vds < Vov) {
            // Triode region: Id = β · (2·Vov·Vds − Vds²) · (1 + λ·Vds)
            const onePlusLVds = 1 + Lambda * Vds;
            const baseId = 2 * Vov * Vds - Vds * Vds;
            Id = Beta * baseId * onePlusLVds;
            gm = 2 * Beta * Vds * onePlusLVds;
            gds = 2 * Beta * (Vov - Vds) * onePlusLVds + Beta * baseId * Lambda;
        }
        else {
            // Saturation: Id = β · Vov² · (1 + λ·Vds)
            const onePlusLVds = 1 + Lambda * Vds;
            Id = Beta * Vov * Vov * onePlusLVds;
            gm = 2 * Beta * Vov * onePlusLVds;
            gds = Beta * Vov * Vov * Lambda;
        }
    }
    // Admittance stamp — gate is current-free (ideal JFET DC),
    // drain carries Id, source carries -Id. Polarity sign cancels
    // in the admittance block (same trick as MOSFET).
    ctx.addY(D, G, gm);
    ctx.addY(D, D, gds);
    ctx.addY(D, S, -(gm + gds));
    ctx.addY(S, G, -gm);
    ctx.addY(S, D, -gds);
    ctx.addY(S, S, gm + gds);
    // Norton equivalent. Polarity flip applied to RHS only.
    const IeqD = sign * (Id - (gm * vG + gds * vD - (gm + gds) * vS));
    const IeqS = -IeqD;
    if (D >= 0)
        ctx.rhs[D] = (ctx.rhs[D] ?? 0) - IeqD;
    if (S >= 0)
        ctx.rhs[S] = (ctx.rhs[S] ?? 0) - IeqS;
}
export const jfetNDCStamp = (ctx, component) => {
    jfetStampCommon(ctx, component, "n");
};
export const jfetPDCStamp = (ctx, component) => {
    jfetStampCommon(ctx, component, "p");
};
export const jfetNDCStampDefinition = defineDCStamp("jfet-n", jfetNDCStamp, []);
export const jfetPDCStampDefinition = defineDCStamp("jfet-p", jfetPDCStamp, []);
//# sourceMappingURL=jfet.js.map